import { z } from "zod";
import type { SceneDocument } from "./schema";

/**
 * Guides ("Hilfslinien") and the layout grid ("Satzspiegel") of a document
 * (docs/04-dokumentformat.md). Both are editor aids: they are saved with the
 * document, shared live and undoable, but never rendered (exports, thumbnails
 * and share links ignore them). They apply to every page of the document.
 *
 * Values are document pixels like everything else; positions count from the
 * trim edge (top-left corner of the page, not of the bleed).
 */

export const GUIDE_LIMIT = 200;

export const guideSchema = z.object({
  id: z.string().min(1).max(64),
  /** `x`: vertical line at `position` on the x axis; `y`: horizontal line. */
  axis: z.enum(["x", "y"]),
  /** Document px from the trim edge; may lie in the bleed or beside the page. */
  position: z.number().min(-20_000).max(40_000),
});

const gridLength = z.number().min(0).max(20_000);

export const layoutGridSchema = z.object({
  /** Distance of the type area from the trim edges. */
  margins: z.object({
    top: gridLength,
    right: gridLength,
    bottom: gridLength,
    left: gridLength,
  }),
  /** The settings change all four margins at once ("alle gleich"). */
  linked: z.boolean().optional(),
  columns: z.number().int().min(1).max(24),
  /** Space between two columns ("Spaltenabstand"). */
  columnGutter: gridLength,
  rows: z.number().int().min(1).max(24),
  rowGutter: gridLength,
});

export type Guide = z.infer<typeof guideSchema>;
export type LayoutGrid = z.infer<typeof layoutGridSchema>;

/** A band of the grid (a column or a row): start and end on its axis. */
export interface GridBand {
  start: number;
  end: number;
}

export interface LayoutGridGeometry {
  /** Type area inside the margins (width/height may be 0 when margins overlap). */
  area: { x: number; y: number; width: number; height: number };
  columns: GridBand[];
  rows: GridBand[];
}

function bands(start: number, length: number, count: number, gutter: number): GridBand[] {
  if (length <= 0) return [];
  if (count <= 1) return [{ start, end: start + length }];
  const size = (length - gutter * (count - 1)) / count;
  if (size <= 0) return [{ start, end: start + length }];
  return Array.from({ length: count }, (_, index) => {
    const from = start + index * (size + gutter);
    return { start: from, end: from + size };
  });
}

/** Type area, columns and rows of a grid on a page of the given size. */
export function layoutGridGeometry(
  grid: LayoutGrid,
  width: number,
  height: number,
): LayoutGridGeometry {
  const { top, right, bottom, left } = grid.margins;
  const area = {
    x: left,
    y: top,
    width: Math.max(0, width - left - right),
    height: Math.max(0, height - top - bottom),
  };
  return {
    area,
    columns: bands(area.x, area.width, grid.columns, grid.columnGutter),
    rows: bands(area.y, area.height, grid.rows, grid.rowGutter),
  };
}

/** Lines of the grid elements snap to: margins and the edges of all columns and rows. */
export function layoutGridLines(
  grid: LayoutGrid,
  width: number,
  height: number,
): { x: number[]; y: number[] } {
  const geometry = layoutGridGeometry(grid, width, height);
  const unique = (values: number[]) =>
    [...new Set(values.map((value) => Math.round(value * 1000) / 1000))].sort((a, b) => a - b);
  const { area } = geometry;
  return {
    x: unique([
      area.x,
      area.x + area.width,
      ...geometry.columns.flatMap((band) => [band.start, band.end]),
    ]),
    y: unique([
      area.y,
      area.y + area.height,
      ...geometry.rows.flatMap((band) => [band.start, band.end]),
    ]),
  };
}

/** Snap positions of the guides and the grid (the visible ones only). */
export function layoutSnapPositions(
  doc: SceneDocument,
  visible: { guides: boolean; grid: boolean },
): { x: number[]; y: number[] } {
  const x: number[] = [];
  const y: number[] = [];
  if (visible.guides) {
    for (const guide of doc.guides ?? []) (guide.axis === "x" ? x : y).push(guide.position);
  }
  if (visible.grid && doc.layoutGrid) {
    const lines = layoutGridLines(doc.layoutGrid, doc.format.width, doc.format.height);
    x.push(...lines.x);
    y.push(...lines.y);
  }
  return { x, y };
}

/** A grid with the given margin on all sides, one column and one row. */
export function uniformLayoutGrid(margin: number): LayoutGrid {
  return {
    margins: { top: margin, right: margin, bottom: margin, left: margin },
    linked: true,
    columns: 1,
    columnGutter: 0,
    rows: 1,
    rowGutter: 0,
  };
}

/** Starting point for a new grid: the safety margin in print, 5 % of the shorter side otherwise. */
export function defaultLayoutGrid(doc: SceneDocument): LayoutGrid {
  const print = doc.format.print;
  if (print && print.safeMm > 0) {
    return uniformLayoutGrid((print.safeMm * doc.format.width) / print.widthMm);
  }
  return uniformLayoutGrid(Math.round(Math.min(doc.format.width, doc.format.height) * 0.05));
}

/**
 * Carries guides and grid over into a transferred document (`target` already
 * has the new format). Guides keep their relative position on the page, like
 * the elements do. Margins and gutters keep their physical size between two
 * print formats (5 mm stay 5 mm); otherwise they scale with the content (the
 * smaller of both size ratios).
 */
export function transferLayout(source: SceneDocument, target: SceneDocument): void {
  const sx = target.format.width / source.format.width;
  const sy = target.format.height / source.format.height;
  if (source.guides) {
    target.guides = source.guides.map((guide) => ({
      ...guide,
      position: Math.round(guide.position * (guide.axis === "x" ? sx : sy) * 1000) / 1000,
    }));
  }
  if (source.layoutGrid) {
    const from = source.format.print;
    const to = target.format.print;
    const factor =
      from && to
        ? (target.format.width / to.widthMm) * (from.widthMm / source.format.width)
        : Math.min(sx, sy);
    const grid = source.layoutGrid;
    const scale = (value: number) => Math.round(value * factor * 100) / 100;
    target.layoutGrid = {
      ...grid,
      margins: {
        top: scale(grid.margins.top),
        right: scale(grid.margins.right),
        bottom: scale(grid.margins.bottom),
        left: scale(grid.margins.left),
      },
      columnGutter: scale(grid.columnGutter),
      rowGutter: scale(grid.rowGutter),
    };
  }
}
